import React from 'react';
import { Box, Container, Grid, Typography, Card, CardContent, Chip, Stack, Divider, Button } from '@mui/material';
import { motion } from 'framer-motion';
import DownloadIcon from '@mui/icons-material/Download';
import BusinessIcon from '@mui/icons-material/Business';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ContactsIcon from '@mui/icons-material/Contacts';
import { companyProfile } from '../../data/companyProfile';

const sectionVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const Capabilities = () => {
  const { companyOverview, coreCompetencies, pastPerformance, differentiators, identifiers, naicsCodes, certifications, keyClients } = companyProfile;

  const downloadPDF = (filename, displayName) => {
    const link = document.createElement('a');
    link.href = `/${filename}`;
    link.download = displayName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box component="section" id="capabilities" sx={{ py: { xs: 8, md: 10 }, backgroundColor: 'background.default' }}>
      <Container>
        <motion.div variants={sectionVariants} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, color: 'text.primary' }}>
            Capability Statement
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 4 }}>
            Established in {companyOverview.establishedYear}, {companyOverview.name} delivers full-service mechanical solutions across the New York metropolitan area.
          </Typography>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => downloadPDF('ANR Capability Statement.pdf', 'ANR_Capability_Statement.pdf')}
              sx={{ fontWeight: 600 }}
            >
              Download Capability Statement
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => downloadPDF('SKM_C36825110521000.pdf', 'ANR_Professional_Certifications.pdf')}
              sx={{ fontWeight: 600 }}
            >
              Download Certifications
            </Button>
          </Stack>
        </motion.div>

        <Grid container spacing={4}>
          {/* Company Overview */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div variants={sectionVariants} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
              <Card elevation={1} sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <BusinessIcon color="primary" />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Company Overview</Typography>
                  </Stack>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {companyOverview.summary}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
                    {naicsCodes.map((n) => (
                      <Chip key={n.code} label={`NAICS ${n.code} — ${n.label}`} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Differentiators */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div variants={sectionVariants} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
              <Card elevation={1} sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <WorkspacePremiumIcon color="primary" />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Differentiators</Typography>
                  </Stack>
                  <Stack spacing={1.5}>
                    {differentiators.map((diff, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <FactCheckIcon sx={{ color: 'secondary.main', fontSize: 20, mt: 0.3 }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{diff}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Core Competencies */}
          <Grid size={{ xs: 12 }}>
            <motion.div variants={sectionVariants} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
              <Card elevation={1} sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <EngineeringIcon color="primary" />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Core Competencies</Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    {coreCompetencies.map((item) => (
                      <Grid key={item.title} size={{ xs: 12, md: 6, lg: 4 }}>
                        <Box sx={{ p: 2, borderRadius: 2, background: 'linear-gradient(180deg, rgba(25,118,210,0.06), rgba(25,118,210,0.01))', border: '1px solid rgba(25,118,210,0.15)' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>{item.title}</Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item.description}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Past Performance */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div variants={sectionVariants} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
              <Card elevation={1} sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <WorkspacePremiumIcon color="primary" />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Past Performance</Typography>
                  </Stack>
                  <Grid container spacing={1}>
                    {pastPerformance.map((p) => (
                      <Grid key={p} size={{ xs: 6 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>• {p}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Certifications & IDs */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div variants={sectionVariants} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
              <Card elevation={1} sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <WorkspacePremiumIcon color="primary" />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Certifications & IDs</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2 }}>
                    {certifications.map((c) => (
                      <Chip key={c} label={c} variant="outlined" size="small" />
                    ))}
                  </Stack>
                  <Divider sx={{ my: 1 }} />
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>Federal ID: {identifiers.federalId}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>DOL NO: {identifiers.dolNo}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>DUNS NO: {identifiers.dunsNo}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Key Clients & Contact */}
          <Grid size={{ xs: 12 }}>
            <motion.div variants={sectionVariants} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
              <Card elevation={1} sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 7 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <ContactsIcon color="primary" />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>Key Clients</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                        {keyClients.map((client) => (
                          <Chip key={client} label={client} color="default" variant="outlined" sx={{ mb: 1 }} />
                        ))}
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <ContactsIcon color="primary" />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>Contact</Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Contact details are available in the footer for quick access.
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Capabilities;